// src/components/subscription/CancelSubscriptionModal.js
import React, { useState } from 'react';
import { XMarkIcon, ExclamationTriangleIcon } from '@heroicons/react/24/solid';
import ActionButton from '../common/ActionButton';
import { OptimizedImage } from '../../utils/imageManager';

const CancelSubscriptionModal = ({ onClose, onConfirm, style, onMouseDown }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm();
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div 
      className="bg-background-secondary rounded-2xl shadow-2xl w-full max-w-md border border-border border-t-4 overflow-hidden"
      style={{
        borderTopColor: 'var(--color-stat-streak)',
        ...style
      }}
      onMouseDown={onMouseDown}
    >
      {/* Glassmorphic header */}
      <div 
        className="relative p-4 flex justify-between items-center overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, var(--color-stat-streak)15, var(--color-stat-streak-secondary)25)',
        }}
      >
        {/* Glassmorphic overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-70 dark:from-white/5 dark:opacity-40" />

        {/* Content */}
        <div className="relative z-10 flex items-center">
          <div 
            className="w-8 h-8 rounded-lg flex items-center justify-center mr-3 backdrop-blur-sm border border-white/20"
            style={{
              background: 'var(--color-stat-streak)',
              boxShadow: '0 4px 12px var(--color-stat-streak)30'
            }}
          >
            <ExclamationTriangleIcon className="h-5 w-5 text-white drop-shadow-sm" />
          </div>
          <h3 className="text-lg font-bold text-content-primary">Switch to Free Plan</h3>
        </div>

        <button 
          onClick={onClose} 
          className="relative z-10 p-1 rounded-full text-content-secondary hover:bg-background-primary transition-colors"
        >
          <XMarkIcon className="h-6 w-6" />
        </button>
      </div>

      {/* Content section */}
      <div className="p-6 text-center space-y-6">
        {/* Character image with glassmorphic background */}
        <div className="relative inline-block">
          <div 
            className="
              w-28 h-28 rounded-full backdrop-blur-sm border border-border
              flex items-center justify-center overflow-hidden
            "
            style={{
              background: 'var(--color-background-primary)',
              boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)'
            }}
          >
            {/* Subtle glassmorphic overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30 rounded-full" />
            <OptimizedImage 
              imageKey="ACTION_INCORRECT" 
              className="relative z-10 w-20 h-20 object-contain" 
              fallback={<span className="text-4xl">🦊</span>}
            />
          </div>
        </div>

        {/* Warning message */}
        <div 
          className="
            relative p-6 rounded-2xl backdrop-blur-sm border border-border 
            overflow-hidden
          "
          style={{
            background: 'linear-gradient(135deg, var(--color-stat-streak)08, var(--color-stat-streak)15)',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.05)'
          }}
        >
          {/* Glassmorphic overlay */}
          <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50 dark:from-white/5 dark:opacity-30" />
          
          {/* Content */}
          <div className="relative z-10 space-y-3">
            <p className="text-base text-content-secondary leading-relaxed">
              You are about to switch to the Free Plan.
            </p>
            <p className="text-base text-content-secondary leading-relaxed">
              Your premium access will continue until the end of your current billing period.
            </p>
            <p className="font-semibold text-content-primary pt-2">
              Are you sure you want to proceed?
            </p>
          </div>
        </div>
      </div>

      {/* Footer with glassmorphic buttons */}
      <div 
        className="
          px-6 py-4 flex justify-end space-x-4 border-t border-border
        "
        style={{
          background: 'var(--color-background-primary)'
        }}
      >
        <ActionButton
          variant="glassy"
          type="button"
          onClick={onClose}
          disabled={isLoading}
          color="orange"
          className="px-6 py-2 text-sm text-white"
        >
          No, Keep My Plan
        </ActionButton>
        
        <ActionButton
          variant="glassy"
          type="button"
          onClick={handleConfirm}
          disabled={isLoading}
          loading={isLoading}
          color="red"
          className="px-6 py-2 text-sm"
        >
          Yes, Switch to Free
        </ActionButton>
      </div>
    </div>
  );
};

export default CancelSubscriptionModal;
